import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useEffect, useState } from 'react';
import CreateReferral from './components/CreateReferral';
import { isProduction, getServiceUrl } from './utils/environment';
import './App.css';

function App() {
  // The clinic as resolved from the live /api/clinics response, delivered by the
  // clinicSelected event. Nothing here reads localStorage: storage holds only a
  // clinicCode as a restore hint, and reading it directly would be a second,
  // independent copy of the selection that can disagree with what the form has
  // actually resolved and is showing the user.
  const [selectedClinic, setSelectedClinic] = useState<any>(null);
  const [isMobile, setIsMobile] = useState(false);

  // clinicLogo is either { type: 'Buffer', data: [...] } from the API or a plain
  // string. Derived rather than stored so it cannot drift from selectedClinic.
  const selectedClinicLogo = selectedClinic?.clinicLogo
    ? (selectedClinic.clinicLogo.data ?? selectedClinic.clinicLogo)
    : null;

  // Disable right-click only in production.
  // This is its own effect on purpose: it used to share an effect with the
  // clinic logo listeners and returned early in production, which meant those
  // listeners were only ever registered outside production.
  useEffect(() => {
    if (!isProduction()) {
      return;
    }

    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      return false;
    };

    document.addEventListener('contextmenu', handleContextMenu);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  useEffect(() => {
    // Listen for custom events from CreateReferral / SearchClinic. CreateReferral
    // dispatches clinicSelected both on a fresh pick and when it restores a
    // previous selection by code on load, so this covers the initial render too.
    const handleClinicSelected = (e: Event) => {
      setSelectedClinic((e as CustomEvent).detail ?? null);
    };

    const handleClinicCleared = () => {
      setSelectedClinic(null);
    };

    window.addEventListener('clinicSelected', handleClinicSelected);
    window.addEventListener('clinicCleared', handleClinicCleared);

    // The previous 1-second setInterval polling of localStorage is gone: the
    // logo now arrives with the event, so there is nothing to poll for.
    return () => {
      window.removeEventListener('clinicSelected', handleClinicSelected);
      window.removeEventListener('clinicCleared', handleClinicCleared);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Call handleResize initially to set the correct state on mount
    handleResize();
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // The clinic code comes from the resolved clinic, not from storage: the link
  // must point at the clinic the user can actually see selected on the page.
  // Until a clinic is resolved this is empty, which getServiceUrl handles by
  // returning the plain base URL.
  const handleBookAppointment = () => {
    const url = getServiceUrl('booking', selectedClinic?.clinicCode || '');
    window.open(url, '_blank');
  }

  const handleTakeAssessment = () => {
    const url = getServiceUrl('partner', selectedClinic?.clinicCode || '');
    window.open(url, '_blank');
  }

  // Helper function to render the clinic logo
  const renderClinicLogo = () => {
    if (!selectedClinicLogo) {
      console.log('No clinic logo available');
      return null;
    }
    
    try {
      console.log('Logo type:', typeof selectedClinicLogo);
      
      // Check if the logo is an array/buffer or a string
      if (typeof selectedClinicLogo === 'object' && selectedClinicLogo.length) {
        // It's a binary buffer
        console.log('Rendering as binary buffer');
        return `data:image/jpeg;base64,${btoa(String.fromCharCode(...new Uint8Array(selectedClinicLogo)))}`;
      } else if (typeof selectedClinicLogo === 'string') {
        // It's already a string (possibly base64 or URL)
        console.log('Rendering as string');
        return selectedClinicLogo;
      } else {
        console.log('Unknown logo format:', selectedClinicLogo);
      }
      return null;
    } catch (error) {
      console.error('Error rendering clinic logo:', error);
      return null;
    }
  };

  return (
    <>
      <div className="app-container flex flex-col min-h-screen">
        {/* Header */}
        <header className={`app-header w-full ${isMobile ? 'flex justify-between' : 'grid grid-cols-3'}`}>
        {/* Luna Logo - Left Column */}
        <div className="luna-logo-container flex justify-start">
          <img 
            src="/images/WhiteLogo.svg" 
            alt="Luna Logo" 
            className="luna-logo-header h-12 md:h-16" 
          />
        </div>
        
      {/* Navigation Links - Center Column */}
       {!isMobile && (
        <div className="nav-links flex items-center justify-center text-sm md:text-xl text-white">
          <p className="cursor-pointer"
              onClick={() => handleBookAppointment()}
              style={{color: "blue", textDecoration: "underline"}}>
              Book an Appointment
          </p>
          <p className="mx-2">|</p>
          <p className="cursor-pointer"
              onClick={() => handleTakeAssessment()}
              style={{color: "blue", textDecoration: "underline"}}>
              Take Assessment
          </p>
        </div>
       )}
        
        {/* Clinic Logo - Right Column */}
        <div className="clinic-logo-container flex justify-end">
          {selectedClinicLogo && renderClinicLogo() && (
            <img 
              src={renderClinicLogo() || ''}
              alt="Clinic Logo"
              className="clinic-logo h-12 md:h-16 w-auto object-contain"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          )}
        </div>
      </header>
      
      {/* Main Content */}
      {isMobile && (
          <div className="nav-links flex items-center justify-center text-sm md:text-xl text-white">
            <p className="cursor-pointer"
                onClick={() => handleBookAppointment()}
                style={{color: "blue", textDecoration: "underline"}}>
                Book an Appointment
            </p>
            <p className="mx-2">|</p>
            <p className="cursor-pointer"
                onClick={() => handleTakeAssessment()}
                style={{color: "blue", textDecoration: "underline"}}>
                Take Assessment
            </p>
          </div>
        )
      }
      <main className="app-content flex-grow">
        <Router>
          <Routes>
            <Route path="/" element={<CreateReferral />} />
            <Route path="/:subPath" element={<CreateReferral />} />
          </Routes>
        </Router>
      </main>

      {isMobile && (
        <img 
          src="/images/Hipaa.svg" 
          alt="HIPAA Compliant" 
          className="hipaa-logo-mobile h-12"
        />
      )}
    </div>
      
    {/* Footer */}
    {
      !isMobile && <img 
        src="/images/Hipaa.svg" 
        alt="HIPAA Compliant" 
        className="hipaa-logo-footer h-12 md:h-16 lg:h-24" 
      />
    }
  </>
  );
}

export default App;
